'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const tabIndex = searchParams?.get('tab') === 'custom' ? 1 : 0;

  const router = useRouter();
  function onTabSelected(index: number) {
    const tab = index === 1 ? 'custom' : 'demo';
    router.push(`/?tab=${tab}`);
  }

  let tabs = React.Children.map(props.children, (child, index) => {
    return (
      <button
        className="lk-button"
        onClick={() => {
          if (onTabSelected) {
            onTabSelected(index);
          }
        }}
        aria-pressed={tabIndex === index}
      >
        {/* @ts-ignore */}
        {child?.props.label}
      </button>
    );
  });

  return (
    <div className={styles.tabContainer}>
      <div className={styles.tabSelect}>{tabs}</div>
      {/* @ts-ignore */}
      {props.children[tabIndex]}
    </div>
  );
}

function DemoMeetingTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const startMeeting = () => {
    if (e2ee) {
      router.push(`/rooms/${generateRoomId()}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${generateRoomId()}`);
    }
  };
  return (
    <div className={styles.tabContent}>
      <p style={{ margin: 0, color: '#475569', fontSize: '0.95rem', lineHeight: '1.5' }}>
        Inicie instantaneamente uma reunião corporativa criptografada e segura.
      </p>
      <button style={{ marginTop: '0.5rem' }} className="lk-button" onClick={startMeeting}>
        Iniciar Reunião Agora
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          ></input>
          <label htmlFor="use-e2ee" style={{ fontSize: '0.9rem', color: '#334155', cursor: 'pointer' }}>
            Ativar Criptografia de Ponta a Ponta (E2EE)
          </label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label htmlFor="passphrase" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
              Chave de Segurança Compartilhada:
            </label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function CustomConnectionTab(props: { label: string }) {
  const router = useRouter();

  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const serverUrl = formData.get('serverUrl');
    const token = formData.get('token');
    if (e2ee) {
      router.push(
        `/custom/?liveKitUrl=${serverUrl}&token=${token}#${encodePassphrase(sharedPassphrase)}`,
      );
    } else {
      router.push(`/custom/?liveKitUrl=${serverUrl}&token=${token}`);
    }
  };
  return (
    <form className={styles.tabContent} onSubmit={onSubmit}>
      <p style={{ marginTop: 0, color: '#475569', fontSize: '0.95rem' }}>
        Conecte-se a uma sala existente utilizando as credenciais de servidor e Token.
      </p>
      <input
        id="serverUrl"
        name="serverUrl"
        type="url"
        placeholder="URL do Servidor LiveKit: wss://meet.prowel.com.br"
        defaultValue="wss://meet.prowel.com.br"
        required
      />
      <textarea
        id="token"
        name="token"
        placeholder="Token de Acesso à Reunião"
        required
        rows={4}
        style={{ padding: '0.75rem 1rem', fontSize: '0.9rem' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          ></input>
          <label htmlFor="use-e2ee" style={{ fontSize: '0.9rem', color: '#334155', cursor: 'pointer' }}>
            Ativar Criptografia de Ponta a Ponta (E2EE)
          </label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label htmlFor="passphrase" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
              Chave de Segurança Compartilhada:
            </label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>

      <hr style={{ width: '100%', borderColor: '#e2e8f0', marginBlock: '0.5rem' }} />
      <button
        style={{ paddingInline: '1.25rem', width: '100%' }}
        className="lk-button"
        type="submit"
      >
        Conectar à Reunião
      </button>
    </form>
  );
}

export default function Page() {
  return (
    <>
      <main className={styles.main}>
        <div className="header">
          <div className="prowel-badge">Prowel Meet</div>
          <img src="/prowel-logo.svg" alt="Prowel Assinaturas & Meet" />
          <h2>
            Plataforma Corporativa de Videoconferência & Reuniões Virtuais Gerenciadas
          </h2>
        </div>
        <Suspense fallback={<div style={{ color: '#64748b' }}>Carregando...</div>}>
          <Tabs>
            <DemoMeetingTab label="Nova Reunião" />
            <CustomConnectionTab label="Conexão por Token" />
          </Tabs>
        </Suspense>
      </main>
      <footer>
        <div>
          <strong>Prowel LTDA — Engenharia e Soluções Gerenciadas</strong> | CNPJ: 50.812.926/0001-44
        </div>
        <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#0284c7' }}>
          Central de Atendimento: 0800 591 2026 | Suporte Prowel
        </div>
      </footer>
    </>
  );
}
